import { ArrowLeft, Users, Heart, HandHeart, MapPin, Sparkles, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function CommunityConnectionGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-emerald-100 rounded-full">
              <Users className="w-8 h-8 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Community Connection</h1>
              <p className="text-emerald-600">Week 10, Day 6 - Social Connections</p>
            </div>
          </div>

          <div className="prose prose-emerald max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Today, step outside your usual circle and connect with your broader community. Whether through 
              volunteering, attending an event, or simply talking to a neighbor, community involvement 
              creates meaning and belonging.
            </p>

            <div className="bg-emerald-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-emerald-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Benefits of Community Connection
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Creates a sense of purpose and meaning</li>
                <li>• Expands your social network</li>
                <li>• Reduces feelings of isolation</li>
                <li>• Increases happiness and life satisfaction</li>
                <li>• Provides perspective on your own life</li>
                <li>• Builds skills and confidence</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Ways to Connect</h2>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-white border-2 border-emerald-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <HandHeart className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-semibold text-gray-900">Volunteer</h3>
                </div>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Food bank or soup kitchen</li>
                  <li>• Animal shelter</li>
                  <li>• Hospital or nursing home</li>
                  <li>• Environmental cleanup</li>
                  <li>• Mentoring programs</li>
                </ul>
              </div>
              
              <div className="bg-white border-2 border-emerald-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-semibold text-gray-900">Attend Events</h3>
                </div>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Farmers market</li>
                  <li>• Community festivals</li>
                  <li>• Town hall meetings</li>
                  <li>• Art walks or gallery openings</li>
                  <li>• Local sports events</li>
                </ul>
              </div>
              
              <div className="bg-white border-2 border-emerald-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-semibold text-gray-900">Join Groups</h3>
                </div>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Fitness class or running club</li>
                  <li>• Book club</li>
                  <li>• Hobby groups</li>
                  <li>• Religious or spiritual community</li>
                  <li>• Professional associations</li>
                </ul>
              </div>
              
              <div className="bg-white border-2 border-emerald-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-semibold text-gray-900">Local Connections</h3>
                </div>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Chat with neighbors</li>
                  <li>• Support local businesses</li>
                  <li>• Attend school events</li>
                  <li>• Join neighborhood watch</li>
                  <li>• Organize a block party</li>
                </ul>
              </div>
            </div>

            <div className="bg-amber-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Today's Challenge: Meet One New Person
              </h2>
              <p className="text-gray-700 mb-4">
                Whatever activity you choose, make it your goal to have a genuine conversation with at 
                least one new person. Here are some conversation starters:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• "How did you get involved with this?"</li>
                <li>• "Is this your first time here?"</li>
                <li>• "What brought you to this area?"</li>
                <li>• "What do you enjoy most about [this activity]?"</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Finding Opportunities</h2>
            
            <div className="bg-gray-50 rounded-xl p-6 mb-8">
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 font-bold">•</span>
                  <span><strong>VolunteerMatch.org</strong> - Find volunteer opportunities by location</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 font-bold">•</span>
                  <span><strong>Meetup.com</strong> - Local interest groups and events</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 font-bold">•</span>
                  <span><strong>Eventbrite</strong> - Community events and activities</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 font-bold">•</span>
                  <span><strong>Local library</strong> - Free programs and clubs</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 font-bold">•</span>
                  <span><strong>Community center</strong> - Classes and activities</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 font-bold">•</span>
                  <span><strong>Facebook Groups</strong> - Local community pages</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-emerald-100 to-teal-100 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-emerald-800 mb-4">
                Remember
              </h2>
              <p className="text-gray-700">
                Community connection doesn't have to be a big commitment. Even a 15-minute conversation 
                with a neighbor or a few hours at a local event can create a sense of belonging and 
                expand your world. Start small and see where it leads.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
